import React, { useState, useEffect } from 'react';
import { 
  Mail, 
  MessageCircle, 
  Save, 
  Loader2, 
  Plus, 
  Trash2, 
  Edit2,
  Check,
  X,
  Send,
  Clock,
  Calendar,
  Phone,
  Copy,
  CheckCircle2,
  RefreshCw
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const apiRequest = async (endpoint, options = {}) => {
  const token = localStorage.getItem('token');
  const response = await fetch(`${API_URL}/api${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      ...options.headers
    }
  });
  if (!response.ok) throw new Error('Request failed');
  return response.json();
};

export const FollowupConfig = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sequence, setSequence] = useState([]);
  const [editingItem, setEditingItem] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  
  // Configurações de envio automático
  const [envioAutomatico, setEnvioAutomatico] = useState(false);
  const [salvandoSettings, setSalvandoSettings] = useState(false);
  
  // Lembretes de WhatsApp
  const [lembretes, setLembretes] = useState([]);
  const [loadingLembretes, setLoadingLembretes] = useState(false);
  const [enviandoEmails, setEnviandoEmails] = useState(false);
  
  // Emails pendentes
  const [emailsPendentes, setEmailsPendentes] = useState([]);
  const [loadingEmails, setLoadingEmails] = useState(false);
  const [enviandoEmailIndividual, setEnviandoEmailIndividual] = useState(null);
  
  // Farmácias para envio manual
  const [farmacias, setFarmacias] = useState([]);
  const [loadingFarmacias, setLoadingFarmacias] = useState(false);
  const [envioManualDialog, setEnvioManualDialog] = useState(false);
  const [farmaciaEnvioManual, setFarmaciaEnvioManual] = useState(null);
  const [diaEnvioManual, setDiaEnvioManual] = useState(2);

  useEffect(() => {
    fetchConfig();
    fetchSettings();
    fetchLembretes();
    fetchEmailsPendentes();
  }, []);

  const fetchConfig = async () => {
    setLoading(true);
    try {
      const data = await apiRequest('/super-admin/followup/config');
      setSequence(data.sequence || []);
    } catch (error) {
      toast.error('Erro ao carregar configuração');
    } finally {
      setLoading(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const data = await apiRequest('/super-admin/followup/settings');
      setEnvioAutomatico(data.envio_automatico || false);
    } catch (error) {
      console.error('Erro ao carregar settings:', error);
    }
  };

  const toggleEnvioAutomatico = async () => {
    setSalvandoSettings(true);
    const novoValor = !envioAutomatico;
    try {
      await apiRequest(`/super-admin/followup/settings?envio_automatico=${novoValor}&horario_envio=09:00`, {
        method: 'PUT'
      });
      setEnvioAutomatico(novoValor);
      toast.success(novoValor ? 'Envio automático ATIVADO! Emails serão enviados às 9h.' : 'Envio automático desativado');
    } catch (error) {
      toast.error('Erro ao alterar configuração');
    } finally {
      setSalvandoSettings(false);
    }
  };

  const fetchLembretes = async () => {
    setLoadingLembretes(true);
    try {
      const data = await apiRequest('/super-admin/followup/lembretes-whatsapp');
      setLembretes(data.lembretes || []);
    } catch (error) {
      console.error('Erro ao carregar lembretes:', error);
    } finally {
      setLoadingLembretes(false);
    }
  };

  const fetchEmailsPendentes = async () => {
    setLoadingEmails(true);
    try {
      const data = await apiRequest('/super-admin/followup/emails-pendentes');
      setEmailsPendentes(data.emails || []);
    } catch (error) {
      console.error('Erro ao carregar emails:', error);
    } finally {
      setLoadingEmails(false);
    }
  };

  const fetchFarmacias = async () => {
    setLoadingFarmacias(true);
    try {
      const data = await apiRequest('/super-admin/followup/farmacias');
      setFarmacias(data.farmacias || []);
    } catch (error) {
      console.error('Erro ao carregar farmácias:', error);
    } finally {
      setLoadingFarmacias(false);
    }
  };

  const enviarEmailIndividual = async (farmaciaId, dia) => {
    setEnviandoEmailIndividual(`${farmaciaId}-${dia}`);
    try {
      await apiRequest(`/super-admin/followup/enviar-email-teste?farmacia_id=${farmaciaId}&dia=${dia}`, {
        method: 'POST'
      });
      toast.success('Email enviado com sucesso!');
      fetchEmailsPendentes();
    } catch (error) {
      toast.error('Erro ao enviar email');
    } finally {
      setEnviandoEmailIndividual(null);
    }
  };

  const abrirEnvioManual = () => {
    fetchFarmacias();
    setEnvioManualDialog(true);
  };

  const enviarEmailManual = async () => {
    if (!farmaciaEnvioManual) {
      toast.error('Selecione uma farmácia');
      return;
    }
    
    setEnviandoEmailIndividual('manual');
    try {
      await apiRequest(`/super-admin/followup/enviar-email-teste?farmacia_id=${farmaciaEnvioManual}&dia=${diaEnvioManual}`, {
        method: 'POST'
      });
      toast.success('Email enviado com sucesso!');
      setEnvioManualDialog(false);
      setFarmaciaEnvioManual(null);
    } catch (error) {
      toast.error('Erro ao enviar email');
    } finally {
      setEnviandoEmailIndividual(null);
    }
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      await apiRequest('/super-admin/followup/config', {
        method: 'PUT',
        body: JSON.stringify({ sequence })
      });
      toast.success('Configuração salva com sucesso!');
    } catch (error) {
      toast.error('Erro ao salvar configuração');
    } finally {
      setSaving(false);
    }
  };

  const handleEditItem = (item, index) => {
    setEditingItem({ ...item, index });
    setDialogOpen(true);
  };

  const handleSaveItem = () => {
    if (!editingItem) return;
    
    const newSequence = [...sequence];
    if (editingItem.index !== undefined) {
      newSequence[editingItem.index] = {
        dia: editingItem.dia,
        tipo: editingItem.tipo,
        assunto: editingItem.assunto,
        mensagem: editingItem.mensagem,
        ativo: editingItem.ativo
      };
    } else {
      newSequence.push({
        dia: editingItem.dia,
        tipo: editingItem.tipo,
        assunto: editingItem.assunto,
        mensagem: editingItem.mensagem,
        ativo: editingItem.ativo ?? true
      });
      // Ordenar por dia
      newSequence.sort((a, b) => a.dia - b.dia);
    }
    
    setSequence(newSequence);
    setDialogOpen(false);
    setEditingItem(null);
  };

  const handleDeleteItem = (index) => {
    if (!window.confirm('Excluir este item do follow-up?')) return;
    const newSequence = sequence.filter((_, i) => i !== index);
    setSequence(newSequence);
  };

  const handleToggleAtivo = (index) => {
    const newSequence = [...sequence];
    newSequence[index].ativo = !newSequence[index].ativo;
    setSequence(newSequence);
  };

  const handleAddNew = () => {
    setEditingItem({
      dia: 1,
      tipo: 'email',
      assunto: '',
      mensagem: '',
      ativo: true
    });
    setDialogOpen(true);
  };

  const marcarWhatsappEnviado = async (farmaciaId, dia) => {
    try {
      await apiRequest(`/super-admin/followup/marcar-whatsapp-enviado?farmacia_id=${farmaciaId}&dia=${dia}`, {
        method: 'POST'
      });
      toast.success('Marcado como enviado!');
      fetchLembretes();
    } catch (error) {
      toast.error('Erro ao marcar como enviado');
    }
  };

  const copiarMensagem = (mensagem) => {
    navigator.clipboard.writeText(mensagem);
    toast.success('Mensagem copiada!');
  };

  const executarEmails = async () => {
    setEnviandoEmails(true);
    try {
      const result = await apiRequest('/super-admin/followup/executar-emails', {
        method: 'POST'
      });
      if (result.enviados > 0) {
        toast.success(`${result.enviados} email(s) enviado(s)!`);
      } else {
        toast.info('Nenhum email para enviar hoje');
      }
      if (result.erros > 0) {
        toast.warning(`${result.erros} erro(s) no envio`);
      }
    } catch (error) {
      toast.error('Erro ao executar envio de emails');
    } finally {
      setEnviandoEmails(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-8 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-teal-600" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-teal-600" />
              Follow-up de Clientes
            </CardTitle>
            <CardDescription>
              Configure a sequência de emails e lembretes de WhatsApp para novos clientes
            </CardDescription>
          </div>
          <div className="flex items-center gap-4">
            {/* Switch de Envio Automático */}
            <div className="flex items-center gap-2 px-3 py-2 bg-slate-100 dark:bg-slate-800 rounded-lg">
              <Switch 
                checked={envioAutomatico} 
                onCheckedChange={toggleEnvioAutomatico}
                disabled={salvandoSettings}
              />
              <div className="flex flex-col">
                <span className="text-sm font-medium">
                  {salvandoSettings ? (
                    <Loader2 className="w-4 h-4 animate-spin inline mr-1" />
                  ) : null}
                  Envio Automático
                </span>
                <span className="text-xs text-slate-500">
                  {envioAutomatico ? '✅ Ativo às 9h' : '❌ Desativado'}
                </span>
              </div>
            </div>
            
            <Button
              variant="outline"
              onClick={executarEmails}
              disabled={enviandoEmails}
            >
              {enviandoEmails ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Enviar Emails Agora
            </Button>
            <Button onClick={saveConfig} disabled={saving} className="bg-teal-600 hover:bg-teal-700">
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Salvar Configuração
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent>
        <Tabs defaultValue="sequencia">
          <TabsList className="mb-4">
            <TabsTrigger value="sequencia" className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Sequência
            </TabsTrigger>
            <TabsTrigger value="emails" className="flex items-center gap-2">
              <Mail className="w-4 h-4" />
              Emails Hoje
              {emailsPendentes.length > 0 && (
                <Badge variant="default" className="ml-1 bg-blue-600">{emailsPendentes.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="whatsapp" className="flex items-center gap-2">
              <MessageCircle className="w-4 h-4" />
              WhatsApp Hoje
              {lembretes.length > 0 && (
                <Badge variant="destructive" className="ml-1">{lembretes.length}</Badge>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Tab Sequência */}
          <TabsContent value="sequencia">
            <div className="mb-4 flex justify-between items-center">
              <p className="text-sm text-slate-500">
                Configure os dias e mensagens que serão enviadas automaticamente (emails) ou manualmente (WhatsApp)
              </p>
              <Button variant="outline" size="sm" onClick={handleAddNew}>
                <Plus className="w-4 h-4 mr-2" />
                Adicionar
              </Button>
            </div>

            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-16">Dia</TableHead>
                    <TableHead className="w-24">Tipo</TableHead>
                    <TableHead>Assunto</TableHead>
                    <TableHead className="w-20 text-center">Ativo</TableHead>
                    <TableHead className="w-24 text-center">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sequence.map((item, index) => (
                    <TableRow key={index} className={!item.ativo ? 'opacity-50' : ''}>
                      <TableCell className="font-bold">
                        Dia {item.dia}
                      </TableCell>
                      <TableCell>
                        <Badge 
                          variant="outline"
                          className={item.tipo === 'email' 
                            ? 'bg-blue-50 text-blue-700 border-blue-200' 
                            : 'bg-green-50 text-green-700 border-green-200'
                          }
                        >
                          {item.tipo === 'email' ? (
                            <><Mail className="w-3 h-3 mr-1" /> Email</>
                          ) : (
                            <><MessageCircle className="w-3 h-3 mr-1" /> WhatsApp</>
                          )}
                        </Badge>
                      </TableCell>
                      <TableCell className="max-w-xs truncate">
                        {item.assunto}
                      </TableCell>
                      <TableCell className="text-center">
                        <Switch 
                          checked={item.ativo} 
                          onCheckedChange={() => handleToggleAtivo(index)}
                        />
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleEditItem(item, index)}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-red-600 hover:bg-red-50"
                            onClick={() => handleDeleteItem(index)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            <div className="mt-4 p-4 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>💡 Variáveis disponíveis:</strong> {'{nome}'} (primeiro nome do cliente), 
                {' {link_sistema}'} (link do sistema), {'{link_assinatura}'} (link para assinar)
              </p>
            </div>
          </TabsContent>

          {/* Tab WhatsApp Hoje */}
          <TabsContent value="whatsapp">
            <div className="mb-4 flex justify-between items-center">
              <p className="text-sm text-slate-500">
                Lembretes de WhatsApp para enviar manualmente hoje
              </p>
              <Button variant="outline" size="sm" onClick={fetchLembretes} disabled={loadingLembretes}>
                {loadingLembretes ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <RefreshCw className="w-4 h-4 mr-2" />
                )}
                Atualizar
              </Button>
            </div>

            {lembretes.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <MessageCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>Nenhum lembrete de WhatsApp para hoje</p>
              </div>
            ) : (
              <div className="space-y-4">
                {lembretes.map((lembrete, index) => (
                  <div key={index} className="border rounded-lg p-4 bg-green-50/50">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge variant="outline" className="bg-green-100 text-green-800">
                            Dia {lembrete.dia}
                          </Badge>
                          <span className="font-medium">{lembrete.farmacia_nome}</span>
                        </div>
                        <p className="text-sm text-slate-600 mb-2">
                          <strong>Para:</strong> {lembrete.responsavel_nome}
                        </p>
                        <p className="text-sm text-slate-600 mb-2">
                          <strong>WhatsApp:</strong> {lembrete.whatsapp || 'Não informado'}
                        </p>
                        <div className="bg-white rounded p-3 border mt-2">
                          <p className="text-sm whitespace-pre-line">{lembrete.mensagem}</p>
                        </div>
                      </div>
                      <div className="flex flex-col gap-2 ml-4">
                        {lembrete.whatsapp && (
                          <Button
                            size="sm"
                            className="bg-green-600 hover:bg-green-700"
                            onClick={() => {
                              const phone = lembrete.whatsapp.replace(/\D/g, '');
                              const fullPhone = phone.startsWith('55') ? phone : `55${phone}`;
                              window.open(`https://wa.me/${fullPhone}?text=${encodeURIComponent(lembrete.mensagem)}`, '_blank');
                            }}
                          >
                            <Phone className="w-4 h-4 mr-1" />
                            Abrir WhatsApp
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => copiarMensagem(lembrete.mensagem)}
                        >
                          <Copy className="w-4 h-4 mr-1" />
                          Copiar
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-green-600 border-green-200 hover:bg-green-50"
                          onClick={() => marcarWhatsappEnviado(lembrete.farmacia_id, lembrete.dia)}
                        >
                          <CheckCircle2 className="w-4 h-4 mr-1" />
                          Enviado
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Tab Emails Hoje */}
          <TabsContent value="emails">
            <div className="mb-4 flex justify-between items-center">
              <p className="text-sm text-slate-500">
                Emails programados para enviar hoje baseado na data de cadastro dos clientes
              </p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={abrirEnvioManual}>
                  <Send className="w-4 h-4 mr-2" />
                  Envio Manual
                </Button>
                <Button variant="outline" size="sm" onClick={fetchEmailsPendentes} disabled={loadingEmails}>
                  {loadingEmails ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <RefreshCw className="w-4 h-4 mr-2" />
                  )}
                  Atualizar
                </Button>
              </div>
            </div>

            {emailsPendentes.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Mail className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>Nenhum email programado para hoje</p>
                <p className="text-xs mt-2">Use "Envio Manual" para enviar emails para clientes específicos</p>
              </div>
            ) : (
              <div className="space-y-4">
                {emailsPendentes.map((email, index) => (
                  <div key={index} className="border rounded-lg p-4 bg-blue-50/50">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge variant="outline" className="bg-blue-100 text-blue-800">
                            Dia {email.dia}
                          </Badge>
                          <span className="font-medium">{email.farmacia_nome}</span>
                        </div>
                        <p className="text-sm text-slate-600 mb-1">
                          <strong>Para:</strong> {email.responsavel_nome}
                        </p>
                        <p className="text-sm text-slate-600 mb-2">
                          <strong>Email:</strong> {email.email}
                        </p>
                        <p className="text-sm text-slate-600 mb-2">
                          <strong>Assunto:</strong> {email.assunto}
                        </p>
                        <div className="bg-white rounded p-3 border mt-2 max-h-32 overflow-y-auto">
                          <p className="text-sm whitespace-pre-line">{email.mensagem}</p>
                        </div>
                      </div>
                      <div className="flex flex-col gap-2 ml-4">
                        <Button
                          size="sm"
                          className="bg-blue-600 hover:bg-blue-700"
                          onClick={() => enviarEmailIndividual(email.farmacia_id, email.dia)}
                          disabled={enviandoEmailIndividual === `${email.farmacia_id}-${email.dia}`}
                        >
                          {enviandoEmailIndividual === `${email.farmacia_id}-${email.dia}` ? (
                            <Loader2 className="w-4 h-4 animate-spin mr-1" />
                          ) : (
                            <Send className="w-4 h-4 mr-1" />
                          )}
                          Enviar
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => copiarMensagem(email.mensagem)}
                        >
                          <Copy className="w-4 h-4 mr-1" />
                          Copiar
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </CardContent>

      {/* Dialog de Envio Manual */}
      <Dialog open={envioManualDialog} onOpenChange={setEnvioManualDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Envio Manual de Email</DialogTitle>
            <DialogDescription>
              Selecione a farmácia e o tipo de email para enviar
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Farmácia</Label>
              <Select
                value={farmaciaEnvioManual || ''}
                onValueChange={setFarmaciaEnvioManual}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione uma farmácia" />
                </SelectTrigger>
                <SelectContent>
                  {farmacias.map((farm) => (
                    <SelectItem key={farm.farmacia_id} value={farm.farmacia_id}>
                      {farm.farmacia_nome} ({farm.email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Tipo de Email (Dia)</Label>
              <Select
                value={String(diaEnvioManual)}
                onValueChange={(v) => setDiaEnvioManual(parseInt(v))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {sequence.filter(s => s.tipo === 'email').map((item) => (
                    <SelectItem key={item.dia} value={String(item.dia)}>
                      Dia {item.dia} - {item.assunto}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEnvioManualDialog(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={enviarEmailManual} 
              className="bg-blue-600 hover:bg-blue-700"
              disabled={enviandoEmailIndividual === 'manual' || !farmaciaEnvioManual}
            >
              {enviandoEmailIndividual === 'manual' ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Enviar Email
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de Edição */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editingItem?.index !== undefined ? 'Editar' : 'Adicionar'} Item de Follow-up
            </DialogTitle>
            <DialogDescription>
              Configure o dia, tipo e conteúdo da mensagem
            </DialogDescription>
          </DialogHeader>

          {editingItem && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Dia após cadastro</Label>
                  <Input
                    type="number"
                    min="1"
                    value={editingItem.dia}
                    onChange={(e) => setEditingItem({ ...editingItem, dia: parseInt(e.target.value) || 1 })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Tipo</Label>
                  <Select
                    value={editingItem.tipo}
                    onValueChange={(value) => setEditingItem({ ...editingItem, tipo: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="email">
                        <span className="flex items-center gap-2">
                          <Mail className="w-4 h-4 text-blue-600" />
                          Email (automático)
                        </span>
                      </SelectItem>
                      <SelectItem value="whatsapp">
                        <span className="flex items-center gap-2">
                          <MessageCircle className="w-4 h-4 text-green-600" />
                          WhatsApp (manual)
                        </span>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Assunto / Título</Label>
                <Input
                  value={editingItem.assunto}
                  onChange={(e) => setEditingItem({ ...editingItem, assunto: e.target.value })}
                  placeholder="Ex: Boas-vindas ao sistema"
                />
              </div>

              <div className="space-y-2">
                <Label>Mensagem</Label>
                <Textarea
                  value={editingItem.mensagem}
                  onChange={(e) => setEditingItem({ ...editingItem, mensagem: e.target.value })}
                  placeholder="Digite a mensagem..."
                  rows={8}
                />
                <p className="text-xs text-slate-500">
                  Use {'{nome}'} para o nome do cliente, {'{link_sistema}'} e {'{link_assinatura}'} para os links
                </p>
              </div>

              <div className="flex items-center gap-2">
                <Switch
                  checked={editingItem.ativo}
                  onCheckedChange={(checked) => setEditingItem({ ...editingItem, ativo: checked })}
                />
                <Label>Ativo</Label>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSaveItem} className="bg-teal-600 hover:bg-teal-700">
              <Check className="w-4 h-4 mr-2" />
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default FollowupConfig;
